// src/components/subcontractors/tabs/SubFinancialsTab.jsx
import { useSubcontractors } from '../../../context/SubcontractorContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(isoStr) {
  const d = parseLocalDate(isoStr);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function exportCSV(rows, subName) {
  const headers = ['Date', 'Amount', 'Method', 'Job Ref', 'Customer', 'Reference', 'Notes', 'Recorded By'];
  const lines = [
    headers.join(','),
    ...rows.map(r =>
      [
        r.date, r.amount, r.method, r.jobRef, r.customerName,
        r.reference || '', r.notes || '', r.recordedBy || '',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `payments-${subName.replace(/[^a-z0-9-_]/gi, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, valueColor }) {
  return (
    <div className="bg-white dark:bg-[#0F1923] rounded-xl p-4 border border-gray-200 dark:border-[#1E3048]">
      <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mb-1">{label}</p>
      <p className="text-[var(--wm-text-hero)] font-semibold" style={{ color: valueColor || '#F8FAFE' }}>
        {value}
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SubFinancialsTab({ sub }) {
  const { getSubJobs, getJobPaymentSummary, deletePayment } = useSubcontractors();

  const allJobs = getSubJobs(sub.id) ?? [];
  const nonArchivedJobs = allJobs.filter(j => j.status !== 'archived');

  // ── Summary stats ────────────────────────────────────────────────────────────
  const totalContracted = nonArchivedJobs.reduce(
    (sum, job) => sum + Number(job.agreedPrice || 0),
    0
  );

  const totalPaid = nonArchivedJobs.reduce((sum, job) => {
    const payments = job.payments || [];
    return sum + payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  }, 0);

  const outstanding = totalContracted - totalPaid;

  const overdue = nonArchivedJobs.reduce((sum, job) => {
    const { amountOwed, status } = getJobPaymentSummary(job);
    return status === 'overdue' ? sum + Math.max(0, amountOwed) : sum;
  }, 0);

  // ── Flat payment list ─────────────────────────────────────────────────────────
  const allPayments = [];
  for (const job of nonArchivedJobs) {
    const payments = job.payments || [];
    for (const payment of payments) {
      allPayments.push({ ...payment, jobId: job.id, jobRef: job.projectRef, customerName: job.customerName || '' });
    }
  }
  allPayments.sort((a, b) => {
    const da = parseLocalDate(a.date);
    const db = parseLocalDate(b.date);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return db - da;
  });

  // ── CSV rows ──────────────────────────────────────────────────────────────────
  const csvRows = allPayments.map(p => ({
    date: p.date ? p.date.split('T')[0] : '',
    amount: p.amount,
    method: p.method,
    jobRef: p.jobRef,
    customerName: p.customerName,
    reference: p.reference || '',
    notes: p.notes || '',
    recordedBy: p.recordedBy || '',
  }));

  return (
    <div className="space-y-4">

      {/* ── Summary cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total Contracted"
          value={formatCurrency(totalContracted)}
          valueColor="#F8FAFE"
        />
        <StatCard
          label="Total Paid"
          value={formatCurrency(totalPaid)}
          valueColor="#10B981"
        />
        <StatCard
          label="Outstanding"
          value={formatCurrency(outstanding)}
          valueColor={outstanding > 0 ? '#F59E0B' : '#10B981'}
        />
        <StatCard
          label="Overdue"
          value={formatCurrency(overdue)}
          valueColor={overdue > 0 ? '#EF4444' : '#10B981'}
        />
      </div>

      {/* ── Payment ledger ─────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wide">
            Payment History
          </span>
          <button
            type="button"
            onClick={() => exportCSV(csvRows, sub.name)}
            className="px-3 py-1.5 text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] hover:border-[#2E8BF0] rounded-lg transition-colors"
          >
            Export CSV
          </button>
        </div>

        {/* Empty state */}
        {allPayments.length === 0 ? (
          <div className="bg-white dark:bg-[#0F1923] rounded-xl p-8 border border-gray-200 dark:border-[#1E3048] text-center">
            <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No payment records yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allPayments.map(payment => (
              <div
                key={`${payment.jobId}-${payment.id}`}
                className="bg-white dark:bg-[#0F1923] rounded-xl px-4 py-3 border border-gray-200 dark:border-[#1E3048] flex justify-between items-start gap-4"
              >
                {/* Left: amount, method, date */}
                <div className="flex flex-col gap-1 min-w-0">
                  <span className="text-[var(--wm-text-body)] font-semibold text-[#10B981]">
                    {formatCurrency(payment.amount)}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 text-[var(--wm-text-ui)] rounded-full border capitalize border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE]">
                      {payment.method}
                    </span>
                    <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
                      {formatDate(payment.date)}
                    </span>
                  </div>
                </div>

                {/* Right: metadata + delete */}
                <div className="flex flex-col gap-0.5 items-end flex-shrink-0 text-right">
                  <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{payment.jobRef}</span>
                  {payment.recordedBy && (
                    <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">By {payment.recordedBy}</span>
                  )}
                  {payment.reference && (
                    <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#4A6380]">{payment.reference}</span>
                  )}
                  {payment.notes && (
                    <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#4A6380] italic">{payment.notes}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => deletePayment(payment.jobId, payment.id)}
                    className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#4A6380] hover:text-[#EF4444] transition-colors mt-0.5 leading-none"
                    aria-label="Delete payment"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
